import React, { CSSProperties } from 'react';
import { IBlock, IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { get, merge } from 'lodash';
import {
	t,
	resolvePreviewColor,
	getAncestorPinnedListing,
	resolveBoundListing,
	getDynamicImageFieldKind,
	getDynamicImageFit,
} from '@core/utils';
import { BasicBlock } from '@core/components/BasicBlock';
import { classnames } from '@core/utils/classnames';
import { getMergeTagImageUrl } from '../../../utils/getMergeTagImageUrl';
import { normalizeMergeTagKey } from '@core/utils/mergeTagDictionary';
import { normalizeLinkHref } from '@core/utils/normalizeLinkHref';
import {
	AttachedListingData,
	getPreviewResolutionDataSource,
	resolveAttachedListingMergeTags,
	resolveViewerProfile,
} from '../../../utils/resolveAttachedListingMergeTags';

const MERGE_TAG_PATTERN =
	/(\{\{\{\{raw\}\}\}\})?\{\{\s*([^{}]+?)\s*\}\}(\{\{\{\{\/raw\}\}\}\})?/gi;

const STATIC_IMAGE_PLACEHOLDER_URL =
	'https://bay.cdn.backatyou.com/ui/design/email-editor/image-placeholder.png';
const DYNAMIC_IMAGE_PLACEHOLDER_URL =
	'https://bay.cdn.backatyou.com/ui/design/email-editor/dynamic-image-placeholder.png';

/**
 * Empty state for a dynamic image field that has a source but no fallback yet.
 *
 * This is UI chrome, not an asset: it has no shape to reconcile against a box,
 * so it is deliberately kept OUT of the contain/cover/circle pipeline. Running
 * it through those rules squished it in short boxes and blurred it in large
 * ones. Vector so it never degrades, fixed size so it never stretches, and the
 * caption is dropped outright when the box is too small to hold it rather than
 * being allowed to overflow.
 */
function renderDynamicEmptyState(options: {
	widthPx: number | null;
	heightPx: number | null;
	align?: string;
	label: string;
}) {
	const { widthPx, heightPx, align, label } = options;
	const ICON = 28;
	const boxHeight = heightPx ? `${heightPx}px` : '120px';
	// The width field is a constraint, not a hint: an explicit pixel width has to
	// size this box the way it sizes a real asset, or the canvas stops matching
	// what the designer typed. Auto still fills the column. max-width keeps an
	// over-wide value inside it, and the auto margins carry `align` — text-align
	// on the parent cell cannot centre a block that now has a width of its own.
	const boxWidth = widthPx ? `${widthPx}px` : '100%';
	const margin = align === 'left' ? '0' : align === 'right' ? '0 0 0 auto' : '0 auto';
	// Below these the caption cannot sit under the icon without clipping.
	const showLabel = (!heightPx || heightPx >= 96) && (!widthPx || widthPx >= 140);

	const icon =
		`<svg width="${ICON}" height="${ICON}" viewBox="0 0 24 24" fill="none" ` +
		`xmlns="http://www.w3.org/2000/svg" style="display:block;margin:0 auto;">` +
		`<rect x="1" y="1" width="22" height="22" rx="3" stroke="#c9cdd4" stroke-width="1.5" ` +
		`stroke-dasharray="3 2" fill="none"/>` +
		`<text x="12" y="16" text-anchor="middle" font-family="monospace" font-size="10" ` +
		`fill="#86909c">{ }</text>` +
		`</svg>`;

	const caption = showLabel
		? `<div style="margin-top:8px;font-size:11px;line-height:1.4;color:#86909c;` +
			`text-align:center;word-break:normal;overflow-wrap:break-word;">${label}</div>`
		: '';

	return (
		`<div style="width:${boxWidth};max-width:100%;margin:${margin};height:${boxHeight};` +
		// #c9cdd4 matches the icon's stroke — the old #e5e6eb sat one step off the
		// #f7f8fa fill and read as no border at all.
		`background:#f7f8fa;border:1px dashed #c9cdd4;` +
		`box-sizing:border-box;overflow:hidden;">` +
		`<table role="presentation" width="100%" height="100%" cellpadding="0" cellspacing="0" border="0">` +
		`<tr><td align="center" valign="middle" style="padding:8px;">${icon}${caption}</td></tr>` +
		`</table></div>`
	);
}

function escapeHtmlAttribute(value: string) {
	return String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

// Returns the numeric pixels for a fixed pixel height (e.g. "90", "90px" -> 90),
// or null for empty / "auto" / non-pixel values.
function toPixelNumber(value: string | undefined): number | null {
	const match = String(value || '')
		.trim()
		.toLowerCase()
		.match(/^(\d+(?:\.\d+)?)\s*(px)?$/);
	if (!match) return null;
	return Number(match[1]);
}

function resolveDataSourceMergeTags(
	template: string | undefined,
	dataSource?: Record<string, any>,
) {
	if (!template || !dataSource) return template || '';

	return template.replace(MERGE_TAG_PATTERN, match => {
		const keyMatch = match.match(/\{\{\s*([^{}]+?)\s*\}\}/);
		const rawKey = String(keyMatch?.[1] || '');
		const trimmedKey = rawKey.trim();
		const normalizedRawKey = normalizeMergeTagKey(rawKey);
		const normalizedTrimmedKey = normalizeMergeTagKey(trimmedKey);

		const resolvedValue =
			get(dataSource, rawKey) ??
			get(dataSource, trimmedKey) ??
			get(dataSource, normalizedRawKey) ??
			get(dataSource, normalizedTrimmedKey);

		// A genuinely blank resolved value (a real field that exists but is empty)
		// is not a resolved value — leave the raw tag so the caller's own
		// empty/placeholder handling runs, same as an unresolved tag. Never
		// resolve to an empty src.
		if (
			(typeof resolvedValue === 'string' && resolvedValue.trim() !== '') ||
			typeof resolvedValue === 'number' ||
			typeof resolvedValue === 'boolean'
		) {
			return String(resolvedValue);
		}

		if (Array.isArray(resolvedValue)) {
			const firstValue = resolvedValue.find(
				item =>
					(typeof item === 'string' && item.trim() !== '') ||
					typeof item === 'number' ||
					typeof item === 'boolean',
			);
			if (firstValue !== undefined) {
				return String(firstValue);
			}
		}

		return match;
	});
}

export type IImage = IBlockData<
	{
		alt?: string;
		src?: string;
		title?: string;
		href?: string;
		target?: string;
		border?: string;
		height?: string;
		'text-decoration'?: string;
		'text-transform'?: CSSProperties['textTransform'];
		align?: CSSProperties['textAlign'];
		'container-background-color'?: string;
		width?: string;
		padding?: string;
		'border-width'?: string;
		'border-style'?: string;
		'border-color'?: string;
	},
	{
		imageType?: 'static' | 'dynamic';
		dynamicImageTag?: string;
		dynamicImageId?: string;
		selectedListingId?: string;
		selectedListing?: AttachedListingData | null;
	}
>;

export const Image: IBlock<IImage> = createBlock({
	get name() {
		return t('Image');
	},
	type: BasicType.IMAGE,
	create: payload => {
		const defaultData: IImage = {
			type: BasicType.IMAGE,
			data: {
				value: {},
			},
			attributes: {
				align: 'center',
				height: 'auto',
				padding: '0px 0px 0px 0px',
				'container-background-color': 'transparent',
				'border-width': '0px',
				'border-style': 'solid',
				'border-color': '#000000',
				width: 'auto',
				src: '',
			},
			children: [],
		};
		return merge(defaultData, payload);
	},
	validParentType: [BasicType.COLUMN, BasicType.HERO],
	render(params) {
		const { data } = params;
		const isPreviewMode = Boolean(params.dataSource?.previewMode);
		const isDesignAdmin = Boolean(params.dataSource?.isDesignAdmin);
		// Same edit/preview/production split as SingleListing, Listing, and
		// CriteriaBlock — production (and DSA admin edit) must keep the raw
		// {{listing.x}} tags for the send-time pipeline to resolve; only the
		// canvas/preview should show a bound listing's actual values.
		let actualMode: 'edit' | 'preview' | 'production';
		if (params.mode === 'production') {
			actualMode = 'production';
		} else if (isPreviewMode) {
			actualMode = 'preview';
		} else {
			actualMode = 'edit';
		}
		const shouldUseMergeTags =
			actualMode === 'production' || (actualMode === 'edit' && isDesignAdmin);
		// An enclosing Listing Wrapper / listing column WINS — see the Text block.
		const selectedListing =
			getAncestorPinnedListing(params.context, params.idx) ||
			resolveBoundListing(data.data.value);
		const dynamicImageExpression = (data.data.value.dynamicImageTag || '').trim();
		const dynamicImageTag =
			dynamicImageExpression.match(/^\{\{\s*([^{}]+?)\s*\}\}$/)?.[1]?.trim() ||
			dynamicImageExpression.replace(/^\{\{|\}\}$/g, '').trim();
		const explicitImageType = data.data.value.imageType;
		const isDynamicMode =
			explicitImageType === 'dynamic' ||
			(explicitImageType !== 'static' && Boolean(dynamicImageTag));
		const dynamicImageTemplate =
			isDynamicMode && dynamicImageTag ? `{{${dynamicImageTag}}}` : '';
		const dynamicImageId = (data.data.value.dynamicImageId || '').trim();
		const dynamicMarkerId =
			dynamicImageId ||
			(isDynamicMode && dynamicImageTag && params.idx
				? `img_dyn_${String(params.idx).replace(/[^a-zA-Z0-9_-]/g, '_')}`
				: '');
		// Text and link tags may answer from sample data — a name reads better than a
		// blank on canvas. See resolveViewerProfile.
		const realUserProfile = resolveViewerProfile(params.dataSource);
		// A dynamic IMAGE may not. Its whole configuration is "field + fallback", and
		// the fallback is the thing the designer has to supply; letting the sample
		// group answer the field paints a stock headshot over the empty state and the
		// fallback never gets uploaded. Design Manager has no real viewer at all, so
		// every dynamic image there resolved to a generic agent photo instead of the
		// upload prompt. Real viewer only: Office shows the agent's own photo, DM
		// shows the fallback (or the box asking for one).
		const dynamicImageProfile = params.dataSource?.realUserProfile;
		const resolvedSrc = shouldUseMergeTags
			? data.attributes.src || ''
			: resolveAttachedListingMergeTags(data.attributes.src || '', {
			listing: selectedListing,
			userProfile: realUserProfile,
		});
		// A dynamic image stores its field in `dynamicImageTag`, never in `src`, so
		// the src-based resolution above cannot see it — a listing-bound dynamic
		// image would paint the grey placeholder forever. Resolve the tag against
		// the listing this block is bound to, or the one its ancestor listing
		// column pins.
		//
		// Canvas and preview only. Production HTML must keep emitting the raw tag:
		// per LISTING_BINDING_CONTRACT a design stores a listing *reference* and the
		// send pipeline resolves at dispatch time, so baking a CDN url in here would
		// ship a photo that is already stale by the time the blast goes out.
		const boundDynamicSrc =
			(params.mode === 'testing' || isPreviewMode) &&
			isDynamicMode &&
			dynamicImageTemplate &&
			(selectedListing || dynamicImageProfile)
				? resolveAttachedListingMergeTags(dynamicImageTemplate, {
						listing: selectedListing,
						userProfile: dynamicImageProfile,
					})
				: '';
		// An unresolved tag comes back unchanged — treat that as no value so the
		// existing placeholder path still runs.
		const listingDynamicSrc = /\{\{/.test(boundDynamicSrc)
			? ''
			: boundDynamicSrc.trim();

		// A picked listing outranks the sample data. Preview used to prefer the test
		// data source whenever it had a value, so once a listing was selected the
		// preview still showed the sample photo — the one place a user checks
		// before sending was the one place the real image never appeared.
		// Sample data still fills in for a dynamic image with nothing bound yet.
		// Never in rasterMode. The sample data behind this call holds REAL generic
		// photos (BAY_GenericAgent_*, sample listing shots), so it answers every
		// dynamic tag with a picture and nothing downstream can tell that apart from
		// a genuine resolve. The raster is a thumbnail OF the canvas, so a field the
		// canvas is showing as an empty box has to stay empty here — otherwise the
		// card advertises imagery the design does not actually have. Preview & Test
		// keeps the sample fill on purpose: it simulates a send, where those fields
		// really would carry data.
		const resolvedTestingSrc = listingDynamicSrc
			? listingDynamicSrc
			: params.mode === 'testing' &&
				  dynamicImageTemplate &&
				  isPreviewMode &&
				  !params.dataSource?.rasterMode
				? resolveDataSourceMergeTags(
						dynamicImageTemplate,
						getPreviewResolutionDataSource(params.dataSource, selectedListing),
					)
				: resolvedSrc;
		const hasEmptyDynamicImageValueInPreview =
			params.mode === 'testing' &&
			dynamicImageTemplate &&
			isPreviewMode &&
			typeof resolvedTestingSrc === 'string' &&
			resolvedTestingSrc.trim() === '';
		// Last resort: the sample data resolved to nothing, or to a tag it could not
		// fill. A bound listing beats falling straight through to the placeholder.
		// (A listing, when there is one, already won above.)
		const resolvedTestingSrcWithDataSource =
			params.mode === 'testing' &&
			(/\{\{\s*[^{}]+\s*\}\}/.test(resolvedTestingSrc) ||
				hasEmptyDynamicImageValueInPreview)
				? listingDynamicSrc || resolvedSrc
				: resolvedTestingSrc;
		const isResolvedSrcEmpty = !String(resolvedTestingSrcWithDataSource || '').trim();
		const previewSrc =
			params.mode === 'testing' && isResolvedSrcEmpty
				? isDynamicMode
					? DYNAMIC_IMAGE_PLACEHOLDER_URL
					: STATIC_IMAGE_PLACEHOLDER_URL
				: params.mode === 'testing'
					? getMergeTagImageUrl(resolvedTestingSrcWithDataSource, params.mode)
					: // Production/raster: an image with no src would otherwise be src-less
						// (getAdapterAttributesString drops empty attrs, so mjml renders no
						// image). Send the placeholder rectangle so the thumbnail/preview
						// match the canvas — gray for dynamic, raw placeholder for static.
						isResolvedSrcEmpty
						? isDynamicMode
							? DYNAMIC_IMAGE_PLACEHOLDER_URL
							: STATIC_IMAGE_PLACEHOLDER_URL
						: resolvedTestingSrcWithDataSource;
		const resolvedHref = shouldUseMergeTags
			? data.attributes.href || ''
			: resolveAttachedListingMergeTags(data.attributes.href || '', {
			listing: selectedListing,
			userProfile: realUserProfile,
		});

		// Bounded-box sizing for dynamic fields: the fit is a property of which
		// field this is, not a user choice, because the real per-record asset's
		// shape is unknown until send time. Applies to the fallback preview too,
		// so the canvas shows what production will actually do.
		const dynamicFieldKind = isDynamicMode
			? getDynamicImageFieldKind(dynamicImageTag)
			: null;
		const dynamicImageFit = getDynamicImageFit(dynamicFieldKind);

		// Combine separate border attributes into single border attribute
		const borderWidth = data.attributes['border-width'];
		const borderStyle = data.attributes['border-style'];
		const borderColor = resolvePreviewColor(
			data.attributes['border-color'],
			params.mode,
			params.dataSource,
		);

		// Create derived attributes (no mutation)
		let attributes = data.attributes;

		if (borderWidth || borderStyle || borderColor) {
			const combinedBorder = [
				borderWidth || '0px',
				borderStyle || 'solid',
				borderColor || '#000000',
			].join(' ');

			attributes = {
				...data.attributes,
				src: previewSrc,
				href: resolvedHref,
				border: combinedBorder,
			};
		} else {
			attributes = {
				...data.attributes,
				src: previewSrc,
				href: resolvedHref,
			};
		}

		// A circle field is masked, and mj-image emits border-radius inline on the
		// <img>, which every client that matters honours.
		if (dynamicFieldKind === 'circle') {
			attributes = { ...attributes, 'border-radius': '50%' };
		}

		/**
		 * An unconfigured image block collapses instead of shipping the system
		 * placeholder — static with nothing uploaded, dynamic with no source
		 * picked. Emitting nothing is the whole fix: an image that renders no
		 * markup takes no space, and if it was the only thing in its column the
		 * existing orphaned-column rule takes over from there (skip the row, or
		 * leave that one slot blank beside its siblings). A column with other
		 * content keeps rendering normally, with a gap where the photo was.
		 *
		 * Editor canvas is excluded on purpose: the designer still has to be
		 * able to see and select the block in order to configure it.
		 */
		const isUnconfigured = isDynamicMode
			? !dynamicImageTag
			: !String(data.attributes.src || '').trim();
		// A dynamic block with no source collapses in the sendable HTML and in
		// ClientBAY's Preview & Test, which promises exactly what gets sent. It
		// still draws the dashed box on the canvas (the designer has to see and
		// select it), in the raster (a picture of the canvas), and in Design
		// Manager's preview. Static keeps the wider collapse: with no upload there
		// is no box to promise.
		const isOutputMode = params.mode === 'production' || isPreviewMode;
		// Same platform test as getPlaceholder — see the note there.
		const isClientPreview =
			isPreviewMode &&
			!params.dataSource?.rasterMode &&
			!params.dataSource?.isDesignAdmin;
		const collapsesHere = isDynamicMode
			? params.mode === 'production' || isClientPreview
			: isOutputMode;
		if (isUnconfigured && collapsesHere) {
			return null;
		}

		// Dynamic field with a source but nothing to show yet: render the empty
		// state before any of the fit handling below can touch it.
		//
		// Every editing surface gets it — canvas, preview modal and raster all run
		// in 'testing' mode. They are three views of the same in-progress design and
		// showing two different placeholders across them just reads as a bug. Only
		// production is excluded: the sendable HTML keeps the hosted image, since
		// "no fallback uploaded" is a message for the designer, not the recipient.
		if (params.mode === 'testing' && isDynamicMode && isResolvedSrcEmpty) {
			// ClientBAY preview: a dynamic image with no data and no fallback
			// collapses, exactly as it would in the sent email — the dashed box is
			// editor chrome and the recipient never sees it. Same platform test as
			// the unconfigured check above.
			if (isClientPreview) {
				return null;
			}
			return (
				<BasicBlock
					params={{
						...params,
						data: {
							...data,
							children: [],
							attributes: {
								align: attributes.align || 'center',
								padding: attributes.padding,
								'container-background-color':
									attributes['container-background-color'],
							} as any,
						},
					}}
					tag='mj-text'
				>
					{renderDynamicEmptyState({
						widthPx: toPixelNumber(data.attributes.width),
						heightPx: toPixelNumber(data.attributes.height),
						align: attributes.align || 'center',
						// Say what is actually missing: with no source picked the field
						// name is the problem, not the fallback.
						label: dynamicImageTag
							? t('Dynamic image — no fallback uploaded')
							: t('Dynamic image — no source selected'),
					})}
				</BasicBlock>
			);
		}

		// Auto width + a fixed pixel height is the one case mj-image can't render: it
		// forces the <img> to width:100%, squishing it. Render it through mj-text,
		// which leaves the <img> markup untouched, so a bare `height` (no width)
		// scales the width proportionally in every client — and it stays a real,
		// selectable block. max-width:100% keeps a very wide image from overflowing.
		const isAutoWidth =
			!data.attributes.width || data.attributes.width.trim() === 'auto';
		const heightPx = toPixelNumber(data.attributes.height);
		if (isAutoWidth && heightPx) {
			// REVERT MARKER A — bounded-box support on the mj-text path.
			// This branch hand-writes its own <img>, so it misses everything the
			// mj-image branch gets from `attributes`. A dynamic logo legitimately
			// lands here (contain + Auto width is just proportional scaling, which
			// this markup already does correctly); a circle or photo only reaches
			// it via pre-existing content, since both now force explicit dims.
			// Deleting this block restores the previous markup exactly.
			const dynamicFitAttrs =
				params.mode === 'production' && dynamicImageFit
					? ` data-dynamic-fit="${escapeHtmlAttribute(dynamicImageFit)}"` +
						(dynamicFieldKind === 'circle' ? ` data-dynamic-shape="circle"` : '')
					: '';
			const circleStyle = dynamicFieldKind === 'circle' ? 'border-radius:50%;' : '';
			// END REVERT MARKER A

			const dynamicAttrs =
				params.mode === 'production' &&
				isDynamicMode &&
				dynamicImageTag &&
				dynamicMarkerId
					? ` id="${escapeHtmlAttribute(dynamicMarkerId)}" data-dynamic-tag="${escapeHtmlAttribute(
							dynamicImageTag,
						)}" data-dynamic-type="image" data-fallback-src="${escapeHtmlAttribute(
							String(resolvedSrc || ''),
						)}"${dynamicFitAttrs}`
					: '';

			let img =
				`<img src="${escapeHtmlAttribute(String(attributes.src || ''))}"` +
				` alt="${escapeHtmlAttribute(String(attributes.alt || ''))}"` +
				(attributes.title ? ` title="${escapeHtmlAttribute(String(attributes.title))}"` : '') +
				` height="${heightPx}" style="height:${heightPx}px;width:auto;max-width:100%;${circleStyle}border:${attributes.border || '0'}"${dynamicAttrs} />`;

			const linkHref = normalizeLinkHref(resolvedHref);
			if (linkHref) {
				const target = attributes.target
					? ` target="${escapeHtmlAttribute(String(attributes.target))}"`
					: '';
				img = `<a href="${escapeHtmlAttribute(linkHref)}"${target}>${img}</a>`;
			}

			// mj-text only needs alignment/spacing attributes; the selection css-class
			// is added by getAdapterAttributesString from the block idx/type.
			return (
				<BasicBlock
					params={{
						...params,
						data: {
							...data,
							children: [],
							attributes: {
								align: attributes.align || 'center',
								padding: attributes.padding,
								'container-background-color':
									attributes['container-background-color'],
							} as any,
						},
					}}
					tag='mj-text'
				>
					{img}
				</BasicBlock>
			);
		}

		if (
			params.mode === 'production' &&
			isDynamicMode &&
			dynamicImageTag &&
			dynamicMarkerId
		) {
			attributes = {
				...attributes,
				// mj-image drops unrecognized attributes (id/data-*) when MJML compiles
				// it, so only css-class survives to the rendered <img class="...">.
				// JsonToMjml.tsx's mj-html-attributes pass matches on this class to
				// re-attach data-dynamic-tag/data-fallback-src/data-dynamic-fit for
				// the backend send-time swap — setting them here directly would be
				// dead weight, since mj-image would just strip them again.
				'css-class': classnames(attributes['css-class'], dynamicMarkerId),
			};
		}
		return (
			<BasicBlock
				params={{
					...params,
					data: {
						...data,
						attributes,
						// Read by BasicBlock to pick the proxy fit. Derived per render,
						// never persisted on the block.
						data: {
							...data.data,
							value: { ...data.data.value, dynamicImageFit },
						},
					},
				}}
				tag='mj-image'
			/>
		);
	},
});
